<template>
    <component
        :is="disabled ? 'div' : 'form'"
        class="dlm-admin-theme"
        :action="disabled ? undefined : formAction"
        method="POST"
        accept-charset="UTF-8"
        @submit="onSubmit"
    >
        <template v-if="!disabled">
            <input type="hidden" name="_token" :value="csrfToken">
            <input v-if="formMethod === 'PUT'" type="hidden" name="_method" value="PUT">
        </template>

        <div class="panel panel-default">
            <div class="panel-heading"><strong>{{ labels.campaign }}</strong></div>
            <div class="panel-body">
                <div class="row">
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('campaign_name') }">
                        <label class="control-label">{{ labels.campaignName }}*</label>
                        <input
                            v-model="campaign.campaign_name"
                            class="form-control"
                            type="text"
                            name="campaign_name"
                            :disabled="disabled"
                            :readonly="disabled"
                        >
                        <p v-if="fieldError('campaign_name')" class="help-block">{{ fieldError('campaign_name') }}</p>
                    </div>
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('gift_title') }">
                        <label class="control-label">{{ labels.giftTitle }}</label>
                        <input
                            v-model="campaign.gift_title"
                            class="form-control"
                            type="text"
                            name="gift_title"
                            :disabled="disabled"
                            :readonly="disabled"
                        >
                        <p v-if="fieldError('gift_title')" class="help-block">{{ fieldError('gift_title') }}</p>
                    </div>
                    <div class="col-md-2 form-group" :class="{ 'has-error': fieldError('channel') }">
                        <label class="control-label">{{ labels.channel }}*</label>
                        <select
                            v-model="campaign.channel"
                            class="form-control"
                            name="channel"
                            :disabled="disabled"
                        >
                            <option v-for="(label, value) in options.channels" :key="value" :value="value">{{ label }}</option>
                        </select>
                        <p v-if="fieldError('channel')" class="help-block">{{ fieldError('channel') }}</p>
                    </div>
                    <div class="col-md-2 form-group" :class="{ 'has-error': fieldError('store') }">
                        <label class="control-label">{{ labels.store }}</label>
                        <select
                            v-model="campaign.store"
                            class="form-control"
                            name="store"
                            :disabled="disabled"
                        >
                            <option v-for="(label, value) in options.stores" :key="value" :value="value">{{ label }}</option>
                        </select>
                        <p v-if="fieldError('store')" class="help-block">{{ fieldError('store') }}</p>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-4 form-group" :class="{ 'has-error': fieldError('product_gift_ean') }">
                        <product-gift-picker
                            v-model="campaign.product_gift_ean"
                            v-model:product-id="campaign.gift_product_id"
                            v-model:primary-artist="campaign.gift_product_primary_artist"
                            v-model:title="campaign.gift_product_title"
                            :label="labels.giftEan + '*'"
                            :placeholder="labels.searchProductPlaceholder"
                            :search-url="options.productSearchUrl"
                            input-id="gift-campaign-product-picker"
                            ean-name="product_gift_ean"
                            product-id-name="gift_product_id"
                            :disabled="disabled"
                            :readonly="disabled"
                            :error-message="fieldError('product_gift_ean')"
                        ></product-gift-picker>
                    </div>
                    <div class="col-md-4 form-group">
                        <label class="control-label">{{ labels.primaryArtist }}</label>
                        <input
                            v-model="campaign.gift_product_primary_artist"
                            class="form-control"
                            type="text"
                            name="gift_product_primary_artist"
                            readonly
                            :disabled="disabled"
                        >
                    </div>
                    <div class="col-md-4 form-group">
                        <label class="control-label">{{ labels.productTitle }}</label>
                        <input
                            v-model="campaign.gift_product_title"
                            class="form-control"
                            type="text"
                            name="gift_product_title"
                            readonly
                            :disabled="disabled"
                        >
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('valid_from') }">
                        <label class="control-label">{{ labels.validFrom }}*</label>
                        <input
                            v-model="campaign.valid_from"
                            class="form-control"
                            type="datetime-local"
                            name="valid_from"
                            step="60"
                            :disabled="disabled"
                            :readonly="disabled"
                        >
                        <p v-if="fieldError('valid_from')" class="help-block">{{ fieldError('valid_from') }}</p>
                    </div>
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('valid_until') }">
                        <label class="control-label">{{ labels.validUntil }}*</label>
                        <input
                            v-model="campaign.valid_until"
                            class="form-control"
                            type="datetime-local"
                            name="valid_until"
                            step="60"
                            :disabled="disabled"
                            :readonly="disabled"
                        >
                        <p v-if="fieldError('valid_until')" class="help-block">{{ fieldError('valid_until') }}</p>
                    </div>
                    <div class="col-md-2 form-group" :class="{ 'has-error': fieldError('total_quantity') }">
                        <label class="control-label">{{ labels.totalQuantity }}</label>
                        <input
                            v-model="campaign.total_quantity"
                            class="form-control"
                            type="number"
                            min="1"
                            name="total_quantity"
                            :disabled="disabled"
                            :readonly="disabled"
                        >
                        <p v-if="fieldError('total_quantity')" class="help-block">{{ fieldError('total_quantity') }}</p>
                    </div>
                    <div class="col-md-2 form-group" :class="{ 'has-error': fieldError('allocation_policy') }">
                        <label class="control-label">{{ labels.allocationPolicy }}*</label>
                        <select
                            v-model="campaign.allocation_policy"
                            class="form-control"
                            name="allocation_policy"
                            :disabled="disabled"
                        >
                            <option
                                v-for="(label, value) in options.allocationPolicies"
                                :key="value"
                                :value="value"
                            >{{ label }}</option>
                        </select>
                        <p v-if="fieldError('allocation_policy')" class="help-block">{{ fieldError('allocation_policy') }}</p>
                    </div>
                    <div class="col-md-2 form-group" :class="{ 'has-error': fieldError('active') }">
                        <slider-button
                            v-model="campaignActive"
                            :label="labels.active + '*'"
                            :name="disabled ? '' : 'active'"
                            input-id="gift-campaign-active"
                            label-align="vertical"
                            :disabled="disabled"
                        ></slider-button>
                        <p v-if="fieldError('active')" class="help-block">{{ fieldError('active') }}</p>
                    </div>
                </div>
                <div v-if="showQuantitySummary" class="alert alert-info">
                    {{ labels.allocated }}: {{ allocatedQuantity }}
                    · {{ labels.remaining }}: {{ remainingLabel }}
                </div>
            </div>
        </div>

        <gift-campaign-trigger-list
            :triggers="triggers"
            :active-options="options.activeOptions"
            :labels="labels"
            :search-url="options.productSearchUrl"
            :validation-errors="validationErrors"
            :campaign-mode="initialState.mode"
            :disabled="disabled"
            @update:triggers="triggers = $event"
        ></gift-campaign-trigger-list>

        <div class="pull-left">
            <button v-if="!disabled" type="submit" class="btn btn-success">{{ labels.saveForm }}</button>
            <a v-if="disabled && editUrl" :href="editUrl" class="btn btn-success">{{ labels.editCampaign }}</a>
        </div>
        <div class="pull-right">
            <a :href="backUrl" class="btn btn-primary">{{ labels.back }}</a>
        </div>
    </component>
</template>

<script>
import GiftCampaignTriggerList from './GiftCampaignTriggerList.vue';
import SliderButton from '@admin-partials/SliderButton.vue';
import ProductGiftPicker from '../partials/ProductGiftPicker.vue';

export default {
    components: {
        GiftCampaignTriggerList,
        SliderButton,
        ProductGiftPicker,
    },
    props: {
        formAction: {
            type: String,
            default: '',
        },
        formMethod: {
            type: String,
            default: 'POST',
        },
        csrfToken: {
            type: String,
            default: '',
        },
        backUrl: {
            type: String,
            required: true,
        },
        editUrl: {
            type: String,
            default: '',
        },
        initialState: {
            type: Object,
            required: true,
        },
        options: {
            type: Object,
            required: true,
        },
        validationErrors: {
            type: Object,
            default: () => ({}),
        },
        allocatedQuantity: {
            type: Number,
            default: 0,
        },
        remainingQuantity: {
            type: [Number, null],
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            campaign: { ...this.initialState.campaign },
            triggers: this.prepareTriggers(this.initialState.triggers || []),
        };
    },
    computed: {
        labels() {
            return this.options.labels || {};
        },
        showQuantitySummary() {
            return this.initialState.mode === 'edit' || this.initialState.mode === 'show';
        },
        remainingLabel() {
            if (this.remainingQuantity === null) {
                return this.labels.unlimited;
            }

            return String(this.remainingQuantity);
        },
        campaignActive: {
            get() {
                return Boolean(this.campaign.active);
            },
            set(value) {
                this.campaign.active = value ? 1 : 0;
            },
        },
    },
    methods: {
        onSubmit(event) {
            if (this.disabled) {
                event.preventDefault();
            }
        },
        fieldError(field) {
            const messages = this.validationErrors[field];

            return Array.isArray(messages) ? messages[0] : null;
        },
        prepareTriggers(rows) {
            return rows.map((row, index) => ({
                ...row,
                active: Boolean(row.active),
                _key: row.id ? `existing-${row.id}` : `loaded-${index}`,
            }));
        },
    },
};
</script>
